import {
  Box,
  Container,
  Typography,
  IconButton,
} from '@mui/material'
import {
  GitHub as GitHubIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Email as EmailIcon,
} from '@mui/icons-material'
import { FooterProps } from '../types'
import { useRoleAssumption } from '../contexts/RoleAssumptionContext'

interface SocialLink {
  icon: React.ReactNode
  href: string
  label: string
}

const Footer: React.FC<FooterProps> = () => {
  const currentYear = new Date().getFullYear()
  const { isRoleAssumed } = useRoleAssumption()

  const socialLinks: SocialLink[] = [
    { icon: <GitHubIcon />, href: 'https://github.com/veilstream', label: 'GitHub' },
    { icon: <LinkedInIcon />, href: 'https://linkedin.com/company/veilstream', label: 'LinkedIn' },
    { icon: <TwitterIcon />, href: 'https://twitter.com/veilstream', label: 'Twitter' },
    { icon: <EmailIcon />, href: 'mailto:contact@veilstream.com', label: 'Email' },
  ]

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: isRoleAssumed ? '#8B0000' : 'primary.main', // Dark red when role assumed
        borderTop: 1,
        borderColor: isRoleAssumed ? '#6B0000' : 'primary.dark',
        mt: 'auto',
        color: 'white',
      }}
    >
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          {/* Copyright and Links */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="rgba(255, 255, 255, 0.8)">
              © {currentYear} VeilStream. All rights reserved.
            </Typography>
            <Typography
              variant="body2"
              component="a"
              href="/tos"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'none',
                '&:hover': {
                  color: 'white',
                  textDecoration: 'underline',
                }
              }}
            >
              Terms of Service
            </Typography>
            <Typography
              variant="body2"
              component="a"
              href="/privacy"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'none',
                '&:hover': {
                  color: 'white',
                  textDecoration: 'underline',
                }
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              variant="body2"
              component="a"
              href="/tos#refund"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'none',
                '&:hover': {
                  color: 'white',
                  textDecoration: 'underline',
                }
              }}
            >
              Refund Policy
            </Typography>
            <Typography
              variant="body2"
              component="a"
              href="https://pricing.veilstream.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'none',
                '&:hover': {
                  color: 'white',
                  textDecoration: 'underline',
                }
              }}
            >
              Pricing
            </Typography>
          </Box>
          
          {/* Social Links */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {socialLinks.map((social, index) => (
              <IconButton
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                size="small"
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    color: 'white',
                  }
                }}
              >
                {social.icon}
              </IconButton>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default Footer
